import React, { useState, useEffect } from "react";
import {
  Select,
  MenuItem,
  Typography,
  Box,
  TextField,
  Grid,
  Paper,
  FormControl,
  Tooltip,
} from "@mui/material";
import { Map as MapIcon } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { Controller } from "react-hook-form";
import * as yup from "yup";
import { getAllEmirates } from "../../../../api/emirates";

const NewRequest = ({ control, errors }) => {
  const [emirates, setEmirates] = useState([]);
  const [busStops, setBusStops] = useState([]);
  const [routes, setRoutes] = useState([]);

  const API_BASE_URL = process.env.REACT_APP_BASE_API;

  useEffect(() => {
    getAllEmirates()
      .then((data) => setEmirates(data))
      .catch((error) => console.error("Error fetching emirates:", error));
  }, []);

  const fetchRoutes = (emirateId) => {
    fetch(`${API_BASE_URL}routes/by-emirate/${emirateId}`, {
      headers: {
        "ngrok-skip-browser-warning": "true",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) {
          setRoutes(data.data);
        } else {
          setRoutes([]);
        }
      })
      .catch((error) => console.error("Error fetching routes:", error));
  };

  const fetchBusStops = (routeId) => {
    fetch(`${API_BASE_URL}bustops/route/${routeId}`, {
      headers: {
        "ngrok-skip-browser-warning": "true",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) {
          setBusStops(data.data);
        } else {
          setBusStops([]);
        }
      })
      .catch((error) => console.error("Error fetching bus stops:", error));
  };

  return (
    <Box sx={{ width: "75%", margin: "auto", mt: 3 }}>
      <Paper elevation={0} sx={{ border: "1px solid black", p: 2 }}>
        <Box display="flex" alignItems="center" sx={{ bgcolor: "#e8e8e8", p: 1, mb: 2 }}>
          <Typography variant="subtitle1" component="div" sx={{ fontWeight: "bold" }}>
            Please provide the exact location for new transport: (NEW TRANSPORT REQUEST)
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              Confirmed Date of Entry
            </Typography>
            <Controller
              name="confirmedDateOfEntry"
              control={control}
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    {...field}
                    slotProps={{
                      textField: {
                        variant: "standard",
                        size: "small",
                        fullWidth: true,
                        error: !!errors.confirmedDateOfEntry,
                        helperText: errors.confirmedDateOfEntry?.message,
                        sx: {
                          "& .MuiInput-root": {
                            borderBottom: "1px solid black",
                            "&:before, &:after": { display: "none" },
                          },
                          "& input": {
                            fontSize: { xs: "0.6rem", md: "0.8rem", xl: "1.2rem" },
                            textAlign: "center",
                            marginTop: "-5px",
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              Emirates
            </Typography>
            <Controller
              name="emirate"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth variant="standard">
                  <Select
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      fetchRoutes(e.target.value);
                    }}
                    displayEmpty
                    error={!!errors.emirate}
                    inputProps={{ "aria-label": "Select emirate" }}
                    sx={{
                      "&:before": { borderBottom: "1px solid black" },
                      "&:after": { borderBottom: "1px solid black" },
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select emirate
                    </MenuItem>
                    {emirates?.map((emirate) => (
                      <MenuItem key={emirate?.eid} value={emirate?.eid}>
                        {emirate?.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.emirate && (
                    <Typography color="error" variant="caption">
                      {errors.emirate.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              Route
            </Typography>
            <Controller
              name="route"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth variant="standard">
                  <Select
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      fetchBusStops(e.target.value);
                    }}
                    displayEmpty
                    error={!!errors.route}
                    inputProps={{ "aria-label": "Select route" }}
                    sx={{
                      "&:before": { borderBottom: "1px solid black" },
                      "&:after": { borderBottom: "1px solid black" },
                    }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <em>Select route</em>;
                      }
                      const route = routes.find((r) => r.rid === selected);
                      return route ? `${route.routename} - ${route.routeno}` : "";
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>Select route</em>
                    </MenuItem>
                    {routes.map((route) => (
                      <MenuItem
                        key={route.rid}
                        value={route.rid}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>{`${route.routename} - ${route.routeno}`}</span>
                        {route.map && (
                          <Tooltip title="Show route on map" arrow>
                            <MapIcon
                              sx={{
                                marginLeft: "auto",
                                cursor: "pointer",
                                fontSize: "1.2rem",
                                color: "primary.main",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(route.map, "_blank");
                              }}
                            />
                          </Tooltip>
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.route && (
                    <Typography color="error" variant="caption">
                      {errors.route.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              Pick-up / Drop-off Point
            </Typography>
            <Controller
              name="busStop"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth variant="standard">
                  <Select
                    {...field}
                    displayEmpty
                    error={!!errors.busStop}
                    inputProps={{ "aria-label": "Select pick-up / drop-off point" }}
                    sx={{
                      "&:before": { borderBottom: "1px solid black" },
                      "&:after": { borderBottom: "1px solid black" },
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select pick-up / drop-off point
                    </MenuItem>
                    {busStops.map((stop) => (
                      <MenuItem key={stop.sid} value={stop.sid}>
                        {`${stop.pickupcode} - ${stop.bustopname}`}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.busStop && (
                    <Typography color="error" variant="caption">
                      {errors.busStop.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" component="label">
              Address:
            </Typography>
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="standard"
                  error={!!errors.address}
                  helperText={errors.address?.message}
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    "& .MuiInput-root": {
                      borderBottom: "1px solid black",
                      "&:before, &:after": { display: "none" },
                    },
                  }}
                />
              )}
            />
          </Grid>
        </Grid>

        <Typography variant="caption" sx={{ mt: 2, display: "block" }}>
          Note:- Only limited areas are covered, kindly visit Al Saraya website for more information
          - www.alsaraya.ae
        </Typography>
      </Paper>
    </Box>
  );
};

NewRequest.validationSchema = yup.object().shape({
  confirmedDateOfEntry: yup.date().required("Confirmed Date of Entry is required"),
  emirate: yup.string().required("Emirates is required"),
  route: yup.string().required("Route is required"),
  busStop: yup.string().required("Pick-up / Drop-off Point is required"),
  address: yup.string().required("Address is required"),
});

export default NewRequest;
